import React, { useState, useEffect } from 'react';
import { Gallery } from '../../types/Gallery';
import { getOptimizedImageUrl } from '../../utils/CloudinaryAPI';
import { applyHeroImagePositioning, getCSSObjectPosition } from '../../utils/ImagePositioning';
import ShareButton from '../Sharing/ShareButton';
import { generateShareContent } from '../../utils/sharingUrls';
import sitePaths from '../../utils/sitePaths';
import './ImageCard.css';
import '../ui-master-styles.css';

interface ImageCardProps {
  gallery: Gallery;
  showcase?: boolean;
  feature?: boolean;
  className?: string;
  onGalleryClick?: (gallery: Gallery) => void;
  categoryColor?: string;
}

const ImageCard: React.FC<ImageCardProps> = React.memo(({ gallery, showcase = false, feature = false, className = '', onGalleryClick, categoryColor }) => {
  const { name, images, category } = gallery;
  // Always use first image as hero image (simplest approach)
  const heroImage = images && images.length > 0 ? images[0] : { publicId: 'placeholder', url: '/placeholder.jpg', alt: 'No image' };
  const [imageError, setImageError] = useState(false);

  // Force component to recalculate the hero image URL when props change 
  useEffect(() => {
    // Image URL recalculation handled automatically
  }, [name, gallery.heroImage?.url, gallery.heroImage?.publicId]);

  // Theme refresh mechanism for rollover effects
  const cardRef = React.useRef<HTMLAnchorElement>(null);
  
  useEffect(() => {
    const handleThemeUpdate = () => {
      // Force re-evaluation of theme colors by momentarily updating transform, 
      // which forces recalculation of CSS custom properties used in box-shadow/background/etc
      if (cardRef.current) {
        const currentTransform = cardRef.current.style.transform;
        cardRef.current.style.transform = 'translateZ(0.1px)';
        requestAnimationFrame(() => {
          if (cardRef.current) {
            cardRef.current.style.transform = currentTransform;
          }
        });
      }
    };

    window.addEventListener('theme_updated', handleThemeUpdate);
    return () => {
      window.removeEventListener('theme_updated', handleThemeUpdate);
    };
  }, []);

  // Generate optimized image URL based on card type
  const imageUrl = React.useMemo(() => {
    if (!heroImage.publicId || heroImage.publicId === 'placeholder' || imageError) {
      return heroImage.url || heroImage.secure_url || '/placeholder.jpg';
    }
    
    const imageType = feature ? 'hero' : showcase ? 'modal' : 'card';
    const baseUrl = getOptimizedImageUrl(heroImage.publicId, imageType);
    
    // If position is explicitly set to 'default', skip all positioning
    if (gallery.heroImagePosition === 'default') {
      return baseUrl;
    }
    
    // Apply hero image positioning and zoom if available
    // Use much larger dimensions for feature/hero images for maximum impact
    if (gallery.heroImagePosition || gallery.heroImageZoom) {
      return applyHeroImagePositioning(
        baseUrl, 
        gallery.heroImagePosition || 'center',
        feature ? 2000 : showcase ? 1200 : 600,
        feature ? 1400 : showcase ? 900 : 450,
        gallery.heroImageZoom || 1.0,
        gallery.heroImageCustomX,
        gallery.heroImageCustomY,
        gallery.heroImageCropBox
      );
    }
    
    return baseUrl;
  }, [heroImage.publicId, heroImage.url, heroImage.secure_url, feature, showcase, imageError, gallery.heroImagePosition, gallery.heroImageZoom, gallery.heroImageCropBox]);

  // Generate share content - regenerate when gallery hero settings change
  const shareContent = React.useMemo(() => {
    console.log('🔄 ImageCard: Regenerating share content for gallery:', gallery.name);
    return generateShareContent('gallery', gallery);
  }, [gallery, gallery.heroImagePosition, gallery.heroImageZoom, gallery.heroImageCropBox]);

  // Reset states when gallery changes
  useEffect(() => {
    setImageError(false);
  }, [gallery.id, gallery.heroImage.publicId]);

  // Removed getCategoryEmoji function - no icons needed

  const cardClasses = `image-card theme-responsive ${showcase ? 'showcase-card' : ''} ${feature ? 'feature-card' : ''} ${className}`;

  return (
    <a
      ref={cardRef}
      href={sitePaths.galleryPath(gallery)}
      className={cardClasses}
      onClick={(event) => {
        if (!onGalleryClick) return;
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;
        event.preventDefault();
        onGalleryClick(gallery);
      }}
    >
      <div className="image-container">
        <img 
          src={imageUrl} 
          alt={heroImage.alt || name}
          className="gallery-image"
          style={{
            objectPosition: getCSSObjectPosition(gallery.heroImagePosition || 'center')
          }}
          onError={(e) => {
            console.warn(`❌ Image load fail for ${name}: imageSrc=${(e.target as HTMLImageElement).src || '(null)'}`);
            setImageError(true);
          }}
        />
      </div>
      
      {/* SHARE BUTTON - Bottom Right */}
      <div className="gallery-share-button" onClick={(e) => e.stopPropagation()}>
        <ShareButton
          content={shareContent}
          variant="icon"
          size="small"
          platforms={['whatsapp', 'twitter', 'facebook', 'pinterest', 'linkedin']}
          onShare={(platform) => {
            console.log(`Shared gallery "${gallery.name}" on ${platform}`);
          }}
        />
      </div>
      
      {/* INFO BUTTON - Top Left (if full description exists and is different from excerpt) */}
      {gallery.description && gallery.description.length > 50 && (
        <div className="gallery-info-button" onClick={(e) => e.stopPropagation()} title="View full description">
          <i className="fas fa-info-circle"></i>
          <div className="info-tooltip">
            <div className="info-tooltip-content">
              <h4>{name}</h4>
              <p>{gallery.description}</p>
            </div>
          </div>
        </div>
      )}
      
      {/* PROFESSIONAL HOVER OVERLAY - Elegant & Minimal */}
      <div className="image-overlay">
        <div className="overlay-content">
          <h3 className="gallery-title">{name}</h3>
        </div>
      </div>
    </a>
  );
});

export default ImageCard;